<template>
    <div class="order-actions-wrapper">
        <div class="order-actions" :class="{ 'multicart-mode': mode === 'multicart', 'packlist-mode': mode === 'packlist' }">
        
        <!-- PACKLIST MODE LAYOUT -->
        <div v-if="mode === 'packlist'" class="packlist-actions-layout">
            <!-- Left: Icon buttons, Printer Status and Timer -->
            <div class="left-controls-with-timer">
                <div class="icon-buttons">
                    <v-btn icon color="primary" @click="handleFullscreen" v-if="!isDesktop">
                        <v-icon>mdi-fullscreen</v-icon>
                    </v-btn>
                    <v-btn icon color="primary" @click="openSettingsModal">
                        <v-icon>mdi-cog</v-icon>
                    </v-btn>
                    <!-- Compact Printer Status -->
                    <div class="printer-status-compact" :class="{ 'online': currentPrinter?.online, 'offline': !currentPrinter?.online }">
                        <v-icon :color="currentPrinter?.online ? 'success' : 'error'" size="18">
                            {{ currentPrinter?.online ? 'mdi-printer' : 'mdi-printer-off' }}
                        </v-icon>
                        <span class="printer-number">{{ extractPrinterNumber(currentPrinter?.name) }}</span>
                    </div>
                </div>
                <div class="timer-section">
                    <Timer :currentPackstats="currentPackstats" layout="linear" />
                </div>
            </div>
            
            <!-- Right: Action buttons -->
            <div class="right-actions">
                <div
                    v-if="shouldShowMulticartWidget"
                    class="instruction-widget multicart-widget"
                    @click="openMulticartModal"
                >
                    <div class="widget-content clickable">
                        <v-icon color="orange darken-2" class="widget-icon">mdi-cart-variant</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Warenkorbregal</div>
                            <div class="widget-item">Multicart öffnen</div>
                        </div>
                    </div>
                </div>

                <!-- Package Size Selection Widget (packlist mode only, before print) -->
                <div
                    v-if="shouldShowPackageSizeWidget"
                    class="instruction-widget package-size-widget"
                >
                    <div class="widget-content">
                        <v-icon color="orange darken-2" class="widget-icon">mdi-ruler</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Paketgröße wählen:</div>
                            <div class="widget-buttons">
                                <v-btn
                                    @click="selectPackageSize(true)"
                                    :color="under8cm === true ? 'primary' : ''"
                                    :outlined="under8cm !== true"
                                    small
                                    class="size-btn">
                                    <v-icon x-small left>mdi-package-variant-closed</v-icon>
                                    Unter 8cm
                                </v-btn>
                                <v-btn
                                    @click="selectPackageSize(false)"
                                    :color="under8cm === false ? 'primary' : ''"
                                    :outlined="under8cm !== false"
                                    small
                                    class="size-btn">
                                    <v-icon x-small left>mdi-package-variant</v-icon>
                                    Über 8cm
                                </v-btn>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Printer Warning Widget -->
                <div
                    v-if="!currentPrinter && !labelPrinted && !hasMissingItems && packageSizeReady"
                    class="instruction-widget warning-widget"
                    @click="openSettingsModal"
                >
                    <div class="widget-content clickable">
                        <v-icon color="orange darken-2" class="widget-icon">mdi-printer-alert</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Kein Drucker ausgewählt</div>
                            <div class="widget-item">Einstellungen öffnen</div>
                        </div>
                    </div>
                </div>

                <!-- Print Label Widget -->
                <div
                    v-if="shouldShowPrintWidget && !isPrinting"
                    class="instruction-widget print-widget"
                    @click="print()"
                >
                    <div class="widget-content clickable">
                        <v-icon color="white" class="widget-icon">mdi-printer</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Bereit zum Drucken</div>
                            <div class="widget-item">Label drucken</div>
                        </div>
                    </div>
                </div>

                <!-- Loading State: While printing -->
                <div
                    v-if="isPrinting"
                    class="instruction-widget loading-widget"
                >
                    <div class="widget-content">
                        <v-icon color="grey darken-1" class="widget-icon spinning">mdi-loading</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Wird gedruckt...</div>
                            <div class="widget-item">Bitte warten</div>
                        </div>
                    </div>
                </div>

                <!-- Tracking ID Scan Widget (packlist mode with scanner enabled) -->
                <div
                    v-if="shouldShowTrackingWidget"
                    class="instruction-widget tracking-widget scan-instruction"
                >
                    <div class="widget-content">
                        <v-icon color="purple darken-2" class="widget-icon bouncing">mdi-barcode-scan</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">{{ trackingScanPrompt }}</div>
                            <div class="widget-item">{{ tracking ? tracking.substring(0, 8) + '...' : 'Versandlabel scannen' }}</div>
                        </div>
                    </div>
                </div>

                <!-- Pack Widget -->
                <div
                    v-if="shouldShowPackWidget"
                    class="instruction-widget pack-widget"
                    @click="pack"
                >
                    <div class="widget-content clickable">
                        <v-icon color="white" class="widget-icon">mdi-package-variant-closed</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">{{ getPackWidgetLabel }}</div>
                            <div class="widget-item">Jetzt verpacken</div>
                        </div>
                    </div>
                </div>

                <!-- Scan Instructions (packlist mode) -->
                <div
                    v-if="nextItemToScan && packageSizeReady && !isPrinted"
                    class="instruction-widget scan-instruction"
                >
                    <div class="widget-content">
                        <v-icon color="orange darken-2" class="widget-icon">mdi-barcode-scan</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Nächsten Artikel scannen:</div>
                            <div class="widget-item">{{ formatScanInstruction(nextItemToScan) }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- MULTICART MODE LAYOUT -->
        <div v-else class="multicart-actions-layout">
            <!-- Left: Settings, Printer Status and Timer -->
            <div class="left-controls-with-timer">
                <div class="icon-buttons">
                    <v-btn icon color="primary" @click="openSettingsModal">
                        <v-icon>mdi-cog</v-icon>
                    </v-btn>
                    <!-- Compact Printer Status -->
                    <div class="printer-status-compact" :class="{ 'online': currentPrinter?.online, 'offline': !currentPrinter?.online }">
                        <v-icon :color="currentPrinter?.online ? 'success' : 'error'" size="18">
                            {{ currentPrinter?.online ? 'mdi-printer' : 'mdi-printer-off' }}
                        </v-icon>
                        <span class="printer-number">{{ extractPrinterNumber(currentPrinter?.name) }}</span>
                    </div>
                </div>
                <div class="timer-section">
                    <Timer :currentPackstats="currentPackstats" layout="linear" />
                </div>
            </div>
            
            <!-- Right: Widgets -->
            <div class="right-widgets">
                <!-- Reset Scan Progress Widget -->
                <div
                    v-if="!isPrinting"
                    class="instruction-widget reset-scan-widget"
                    @click="resetScanProgress"
                >
                    <div class="widget-content clickable">
                        <v-icon color="orange darken-2" class="widget-icon">mdi-restart</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Scan zurücksetzen</div>
                            <div class="widget-item">Neuer Scan-Vorgang</div>
                        </div>
                    </div>
                </div>

                <!-- Split Package Widget -->
                <div 
                    v-if="!isPrinting && isSplittable"
                    class="instruction-widget split-widget"
                    @click="openSplitModal"
                >
                    <div class="widget-content clickable">
                        <v-icon color="primary" class="widget-icon">mdi-arrow-split-vertical</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Paket teilen</div>
                            <div class="widget-item">Artikel aufteilen</div>
                        </div>
                    </div>
                </div>

                <!-- Scan Instructions (multicart mode) -->
                <div
                    v-if="nextItemToScan && packageSizeReady && !isPrinted"
                    class="instruction-widget scan-instruction"
                >
                    <div class="widget-content">
                        <v-icon color="orange darken-2" class="widget-icon">mdi-barcode-scan</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">Nächsten Artikel scannen:</div>
                            <div class="widget-item">{{ formatScanInstruction(nextItemToScan) }}</div>
                        </div>
                    </div>
                </div>

                <!-- Tracking ID Scan Widget -->
                <div
                    v-if="shouldShowMulticartTrackingWidget"
                    class="instruction-widget tracking-widget"
                >
                    <div class="widget-content">
                        <v-icon color="purple darken-2" class="widget-icon bouncing">mdi-barcode-scan</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">{{ trackingScanPrompt }}</div>
                            <div class="widget-item">{{ tracking ? tracking.substring(0, 8) + '...' : 'Versandlabel scannen' }}</div>
                        </div>
                    </div>
                </div>

                <!-- Pack Widget -->
                <div 
                    v-if="shouldShowPackWidget"
                    class="instruction-widget pack-widget"
                    @click="pack"
                >
                    <div class="widget-content clickable">
                        <v-icon color="white" class="widget-icon">mdi-package-variant-closed</v-icon>
                        <div class="widget-text">
                            <div class="widget-label">{{ getPackWidgetLabel }}</div>
                            <div class="widget-item">Jetzt verpacken</div>
                        </div>
                    </div>
                </div>

                <!-- Print Label Widget -->
                <div 
                    v-if="shouldShowPrintWidget"
                    class="instruction-widget print-widget"
                    :class="{ 'printing': isPrinting }"
                    @click="!isPrinting && print()"
                >
                    <div class="widget-content clickable">
                        <v-icon 
                            color="white" 
                            class="widget-icon"
                            :class="{ 'spinning': isPrinting }"
                        >
                            {{ isPrinting ? 'mdi-loading' : 'mdi-printer' }}
                        </v-icon>
                        <div class="widget-text">
                            <div class="widget-label">{{ isPrinting ? 'Wird gedruckt...' : 'Bereit zum Drucken' }}</div>
                            <div class="widget-item">{{ isPrinting ? 'Bitte warten...' : 'Label drucken' }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        </div>
    </div>
</template>

<script>
import Timer from './Timer.vue';

export default {
    name: 'OrderActions',
    components: {
        Timer
    },
    data() {
        return {
            under8cm: null,  // null = unselected, true = under 8cm, false = over 8cm
            packageSizeSelected: false,
            isProcessingState: false  // Flag for loading states between workflow transitions
        }
    },
    props: {
        // Mode selector
        mode: {
            type: String,
            default: 'multicart',
            validator: value => ['packlist', 'multicart'].includes(value)
        },
        
        isPrintable: {
            type: Boolean,
            required: true
        },
        isPrinting: {
            type: Boolean,
            required: true
        },
        isPrinted: {
            type: Boolean,
            required: true
        },
        nextItemToScan: {
            type: Object,
            default: null
        },
        isSplittable: {
            type: Boolean,
            default: false
        },
        isDevelopmentMode: {
            type: Boolean,
            default: false
        },
        tracking: {
            type: String,
            default: null
        },
        trackingIdScanned: {
            type: Boolean,
            default: false
        },
        scannedTrackingId: {
            type: String,
            default: null
        },
        isSplittable: {
            type: Boolean,
            default: false
        },

        // Packlist-specific props
        hasMissingItems: {
            type: Boolean,
            default: false
        },
        orderIsMulticart: {
            type: Boolean,
            default: false
        },
        scannerEnabled: {
            type: Boolean,
            default: false
        },
        multicartItemsMissingShelf: {
            type: Boolean,
            default: false
        },
        multicartItemsHaveShelves: {
            type: Boolean,
            default: false
        },
        multicartItemsInPacklistNeedShelf: {
            type: Boolean,
            default: false
        },
        currentPrinter: {
            type: Object,
            default: null
        },
        currentPacklistName: {
            type: String,
            default: null
        },
        labelPrinted: {
            type: Boolean,
            default: false
        },
        currentOrder: {
            type: Object,
            default: null
        },
        showPackageSizeSelector: {
            type: Boolean,
            default: true
        },
        isDesktop: {
            type: Boolean,
            default: false
        },
        currentPackstats: {
            type: Object,
            default: () => ({
                progress: 0,
                targetProgress: 40,
                count: 0,
                time: 0,
                packStart: null,
                packEnd: null
            })
        },
        
        // Scanning-related props (packlist mode)
        needScan: {
            type: Boolean,
            default: false
        },
        scannableItems: {
            type: Array,
            default: () => []
        },
        allScannableItemsScanned: {
            type: Boolean,
            default: false
        },
        scannedItemsCount: {
            type: Number,
            default: 0
        },
        totalItemsToScan: {
            type: Number,
            default: 0
        },
        isScanning: {
            type: Boolean,
            default: false
        },

        // Swiss & Prime workflow props
        isSwissOrder: {
            type: Boolean,
            default: false
        },
        swissDocumentsPrinted: {
            type: Boolean,
            default: false
        },
        swissTrackingScan: {
            type: Boolean,
            default: false
        },
        shippingLabelPrinted: {
            type: Boolean,
            default: false
        },
        trackingScanPrompt: {
            type: String,
            default: 'Scan tracking ID from shipping label'
        }
    },
    computed: {
        packageSizeReady() {
            const result = this.currentOrder?.registered || this.packageSizeSelected;
            console.log('📦 packageSizeReady computed:', {
                registered: this.currentOrder?.registered,
                packageSizeSelected: this.packageSizeSelected,
                result: result
            });
            return result;
        },

        // Helper computed to determine if multicart workflow should block other buttons
        multicartShouldBlock() {
            if (!this.orderIsMulticart) {
                return false;
            }

            // Scanner OFF: Check multiple conditions
            if (!this.scannerEnabled) {
                // 1. Items NOT in packlist missing shelves → Block (need multicart modal)
                if (this.multicartItemsMissingShelf) {
                    console.log('🚫 Multicart blocking: Scanner OFF + items NOT in packlist missing shelves');
                    return true;
                }

                // 2. Items NOT in packlist HAVE shelves → Block (need scanner mode ON)
                if (this.multicartItemsHaveShelves) {
                    console.log('🚫 Multicart blocking: Scanner OFF + items NOT in packlist have shelves (need scanning)');
                    return true;
                }

                // 3. All items are IN packlist → Don't block
                console.log('✅ Multicart not blocking: Scanner OFF but all items in packlist (button workflow)');
                return false;
            }

            // Scanner ON: Only check items NOT in packlist (multicartItemsMissingShelf)
            // Items IN packlist don't need shelves or scanning

            // Scanner ON + needScan: Don't block (show scan instructions alongside multicart)
            if (this.needScan) {
                console.log('🔍 Multicart not blocking: Scanner ON + needScan (scanning workflow)');
                return false;
            }

            // Scanner ON + items NOT in packlist missing shelves + no scanning: Block
            if (this.multicartItemsMissingShelf) {
                console.log('🚫 Multicart blocking: Scanner ON but items NOT in packlist are missing shelves');
                return true;
            }

            // Scanner ON + all items in packlist OR have shelves + no scanning: Don't block (button workflow)
            console.log('✅ Multicart not blocking: Scanner ON, all items in packlist or have shelves (button workflow)');
            return false;
        },

        shouldShowMulticartWidget() {
            if (!this.orderIsMulticart || this.mode !== 'packlist') {
                return false;
            }

            // Hide multicart widget once we've moved past the multicart stage:
            // - If printing has started (isPrinting or isPrinted)
            // - If all scannable items are scanned (in scanner workflow)
            if (this.isPrinting || this.isPrinted) {
                console.log('🛒 shouldShowMulticartWidget: Hidden - printing started/completed');
                return false;
            }

            if (this.scannerEnabled && this.needScan && this.allScannableItemsScanned) {
                console.log('🛒 shouldShowMulticartWidget: Hidden - all items scanned in scanner workflow');
                return false;
            }

            // Show multicart widget if:
            // 1. Scanner OFF + items NOT in packlist missing shelves (need shelf assignment)
            // 2. Scanner OFF + items NOT in packlist HAVE shelves (manual multicart workflow)
            // 3. Scanner ON + items NOT in packlist missing shelves (need shelf assignment)
            // 4. Scanner ON + needScan (show alongside scan instructions)
            //
            // DON'T show if all items are in packlist (they don't need shelf assignment or multicart workflow)

            let shouldShow = false;

            if (!this.scannerEnabled) {
                // Scanner OFF: Show if items NOT in packlist are missing shelves OR have shelves
                // (both cases need the multicart workflow)
                shouldShow = this.multicartItemsMissingShelf || this.multicartItemsHaveShelves;
            } else {
                // Scanner ON: Show if items missing shelves OR if need to scan
                shouldShow = this.multicartItemsMissingShelf || this.needScan;
            }

            console.log('🛒 shouldShowMulticartWidget:', {
                orderIsMulticart: this.orderIsMulticart,
                scannerEnabled: this.scannerEnabled,
                multicartItemsMissingShelf: this.multicartItemsMissingShelf,
                multicartItemsHaveShelves: this.multicartItemsHaveShelves,
                needScan: this.needScan,
                isPrinting: this.isPrinting,
                isPrinted: this.isPrinted,
                allScannableItemsScanned: this.allScannableItemsScanned,
                shouldShow
            });

            return shouldShow;
        },

        shouldShowPackageSizeWidget() {
            // Block if multicart workflow should block
            if (this.multicartShouldBlock) {
                return false;
            }

            return this.mode === 'packlist' &&
                   this.currentOrder &&
                   !this.currentOrder.registered &&
                   !this.labelPrinted &&
                   !this.hasMissingItems;
        },

        shouldShowTrackingWidget() {
            // Only show in packlist mode when scanner is enabled
            if (this.mode !== 'packlist') {
                console.log('🚫 shouldShowTrackingWidget: false - not in packlist mode', {
                    mode: this.mode
                });
                return false;
            }

            if (!this.scannerEnabled) {
                console.log('🚫 shouldShowTrackingWidget: false - scanner disabled', {
                    scannerEnabled: this.scannerEnabled
                });
                return false;
            }

            const shouldShow = this.labelPrinted && !this.trackingIdScanned && !this.isDevelopmentMode;

            console.log('📋 shouldShowTrackingWidget evaluation:', {
                labelPrinted: this.labelPrinted,
                trackingIdScanned: this.trackingIdScanned,
                isDevelopmentMode: this.isDevelopmentMode,
                isPrinted: this.isPrinted,
                isPrinting: this.isPrinting,
                nextItemToScan: this.nextItemToScan,
                allScannableItemsScanned: this.allScannableItemsScanned,
                shouldShow,
                timestamp: new Date().toISOString()
            });

            // ALWAYS show tracking widget when scanner enabled (user can choose to scan or use pack button)
            return shouldShow;
        },

        shouldShowMulticartTrackingWidget() {
            // For multicart mode - show tracking widget based on Swiss workflow state
            if (this.mode !== 'multicart') {
                return false;
            }

            // In development mode, can skip tracking
            if (this.isDevelopmentMode) {
                return false;
            }

            // Swiss workflow: Show widget after appropriate print step
            if (this.isSwissOrder) {
                // Show after A4 docs printed (first scan)
                if (this.swissDocumentsPrinted && !this.swissTrackingScan) {
                    return true;
                }
                // Show after shipping label printed (second scan)
                if (this.shippingLabelPrinted && !this.trackingIdScanned) {
                    return true;
                }
                return false;
            }

            // Non-Swiss: Show after label printed
            return this.isPrinted && !this.trackingIdScanned;
        },

        shouldShowPackWidget() {
            if (this.mode === 'multicart') {
                return this.isPrinted && !this.isPrinting && (this.trackingIdScanned || this.isDevelopmentMode);
            } else { // packlist mode
                // Block if multicart workflow should block
                if (this.multicartShouldBlock) {
                    return false;
                }

                // ALWAYS show pack button after label is printed (in both scanner and button mode)
                // User can choose to scan tracking ID or just click pack button
                const shouldShow = this.labelPrinted && !this.isPrinting;

                console.log('📦 shouldShowPackWidget:', {
                    mode: this.mode,
                    labelPrinted: this.labelPrinted,
                    isPrinting: this.isPrinting,
                    shouldShow
                });

                return shouldShow;
            }
        },

        shouldShowPrintWidget() {
            if (this.mode === 'multicart') {
                // Hide print button for Swiss orders (auto-prints A4 documents)
                if (this.isSwissOrder) {
                    return false;
                }
                return this.isPrintable && !this.isPrinted;
            } else { // packlist mode
                // Block if multicart workflow should block
                if (this.multicartShouldBlock) {
                    return false;
                }

                const packageSizeReady = this.currentOrder?.registered || this.packageSizeSelected;

                // SMART WORKFLOW: Use scanner workflow only if scanner enabled AND there are scannable items
                const useScannerWorkflow = this.scannerEnabled && this.needScan;

                console.log('🖨️ shouldShowPrintWidget:', {
                    scannerEnabled: this.scannerEnabled,
                    needScan: this.needScan,
                    useScannerWorkflow: useScannerWorkflow,
                    labelPrinted: this.labelPrinted,
                    hasMissingItems: this.hasMissingItems,
                    isPrintable: this.isPrintable,
                    packageSizeReady: packageSizeReady,
                    multicartShouldBlock: this.multicartShouldBlock
                });

                if (useScannerWorkflow) {
                    // SCANNER WORKFLOW: Hide print button (will auto-print after scanning)
                    console.log('🖨️ Using SCANNER workflow - hiding print button');
                    return false;
                } else {
                    // BUTTON WORKFLOW: Show print button when ready
                    const shouldShow = !this.labelPrinted && !this.hasMissingItems && this.isPrintable && packageSizeReady;
                    console.log('🖨️ Using BUTTON workflow - print button:', shouldShow);
                    return shouldShow;
                }
            }
        },

        getPackWidgetLabel() {
            if (this.mode === 'multicart') {
                return this.trackingIdScanned ? 'Tracking ID gescannt!' : 'Bereit zum Verpacken';
            } else {
                // Packlist mode
                if (this.scannerEnabled && this.trackingIdScanned) {
                    return 'Tracking ID gescannt!';
                }
                return 'Label gedruckt!';
            }
        }
    },

    watch: {
        currentOrder: {
            handler(newVal, oldVal) {
                // Reset package size selection when order changes
                if (newVal && oldVal && newVal.id !== oldVal.id) {
                    this.under8cm = null;
                    this.packageSizeSelected = false;
                    console.log('📦 Package size reset for new order');
                }
            },
            deep: true
        },
        labelPrinted: {
            handler(newVal, oldVal) {
                console.log('🏷️ labelPrinted changed:', {
                    old: oldVal,
                    new: newVal,
                    scannerEnabled: this.scannerEnabled,
                    trackingIdScanned: this.trackingIdScanned,
                    shouldShowTrackingWidget: this.shouldShowTrackingWidget
                });

                // Force update to ensure UI reflects the change
                if (newVal && !oldVal) {
                    this.$nextTick(() => {
                        console.log('🔄 Forcing UI update after labelPrinted changed to true');
                        this.$forceUpdate();
                    });
                }
            },
            immediate: false
        }
    },

    methods: {
        print() {
            if (this.mode === 'packlist') {
                this.$emit('print', this.under8cm);
            } else {
                this.$emit('print');
            }
        },
        openSplitModal() {
            this.$emit('open-split-modal');
        },
        pack() {
            console.log('🎯 OrderActions: pack() called - emitting pack event');
            this.$emit('pack');
        },
        resetScanProgress() {
            this.$emit('reset-scan-progress');
        },
        openSettingsModal() {
            this.$emit('open-settings-modal');
        },
        handleFullscreen() {
            this.$emit('handle-fullscreen');
        },
        openMulticartModal() {
            this.$emit('open-multicart-modal');
        },
        formatScanInstruction(item) {
            if (!item) return '';
            
            // Create instruction like "box_waldfreunde - Tina" or just SKU if no name
            const baseName = item.sku || '';
            const personalization = item.translated?.line1 || '';
            
            // Add quantity info if more than 1 or partially scanned
            const remaining = item.qty - item.scannedCount;
            const qtyInfo = item.qty > 1 ? ` (${remaining}/${item.qty})` : '';
            
            return `${baseName}${personalization}${qtyInfo}`;
        },
        extractPrinterNumber(printerName) {
            if (!printerName) return '-';
            
            // Extract number from printer name
            // Examples: "Printer 1", "DYMO #2", "Drucker 3" -> "1", "2", "3"
            const match = printerName.match(/\d+/);
            return match ? match[0] : '-';
        },
        handleSizeChange(value) {
            this.under8cm = value;
        },
        selectPackageSize(value) {
            this.under8cm = value;
            this.packageSizeSelected = true;
            console.log('📦 Package size selected:', value ? 'Under 8cm' : 'Over 8cm');
            // Store in localStorage for convenience (but won't auto-load)
            localStorage.setItem('under8cm', JSON.stringify(value));
        }
    }
}
</script>

<style scoped>
/* 
 * ================================================
 * OrderActions - Standalone Component Styles
 * ================================================
 * - Scoped styles to prevent conflicts
 * - No external CSS dependencies
 * - Self-contained layout system
 * ================================================
 */
/* Base Order Actions Container - Standalone */
.order-actions {
    background-color: #f8f9fa;
    border-top: 1px solid #e0e0e0;
    box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
    flex-shrink: 0;
    box-sizing: border-box;
    position: relative;
    z-index: 10;
    width: 100%;
}

/* Packlist Actions Layout */
.packlist-actions-layout {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    gap: 16px;
}

.packlist-actions-layout .left-controls-with-timer {
    display: flex;
    gap: 16px;
    align-items: center;
    flex: 1;
    max-width: 450px;
}

.packlist-actions-layout .icon-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
}

/* Compact Printer Status */
.printer-status-compact {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #e0e0e0;
    transition: all 0.3s ease;
}

.printer-status-compact.online {
    border-color: #4caf50;
    background: rgba(76, 175, 80, 0.1);
}

.printer-status-compact.offline {
    border-color: #f44336;
    background: rgba(244, 67, 54, 0.1);
}

.printer-status-compact .printer-number {
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 12px;
    font-weight: 700;
    color: #333;
}

.printer-status-compact.online .printer-number {
    color: #2e7d32;
}

.printer-status-compact.offline .printer-number {
    color: #c62828;
}

.packlist-actions-layout .timer-section {
    flex: 1;
    min-width: 200px;
}

.packlist-actions-layout .right-actions {
    display: flex;
    gap: 12px;
    align-items: center;
    flex: 0 0 auto;
}

/* Multicart Actions Layout */
.multicart-actions-layout {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    gap: 16px;
}

.multicart-actions-layout .left-controls-with-timer {
    display: flex;
    gap: 16px;
    align-items: center;
    flex: 1;
    max-width: 450px;
}

.multicart-actions-layout .icon-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
}

.multicart-actions-layout .timer-section {
    flex: 1;
    min-width: 200px;
}

.multicart-actions-layout .right-widgets {
    display: flex;
    gap: 12px;
    align-items: center;
    flex: 0 0 auto;
}

/* Widget Styles */
.instruction-widget {
    border-radius: 12px;
    padding: 8px;
    min-width: 200px;
    border: 2px dashed;
    transition: all 0.3s ease;
}

.widget-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.widget-content.clickable {
    cursor: pointer;
}

.widget-content.clickable:hover {
    transform: translateY(-2px);
}

.widget-icon {
    font-size: 32px !important;
    animation: bounce 2s ease-in-out infinite;
}

.widget-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.widget-label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.widget-item {
    font-size: 14px;
    font-weight: 700;
    word-break: break-word;
}

/* Scan Instruction Widget (Orange) */
.scan-instruction {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-color: #ff9800;
    animation: pulse-scan 2s ease-in-out infinite;
}

.scan-instruction .widget-label {
    color: #f57c00;
}

.scan-instruction .widget-item {
    color: #e65100;
}

/* Pack Widget (Green) */
.pack-widget {
    background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
    border-color: #388e3c;
    animation: pulse-pack 2s ease-in-out infinite;
}

.pack-widget .widget-label {
    color: #fff;
}

.pack-widget .widget-item {
    color: #fff;
}

/* Print Widget (Green) */
.print-widget {
    background: linear-gradient(135deg, #0062ff 0%, #0062ff 100%);
    border-color: #388e3c;
    animation: pulse-print 2s ease-in-out infinite;
}

.print-widget.printing {
    background: linear-gradient(135deg, #66bb6a 0%, #81c784 100%);
    animation: pulse-printing 1s ease-in-out infinite;
}

.warning-widget {
    background: linear-gradient(135deg, #ff9800 0%, #ffa726 100%);
    border-color: #f57c00;
    animation: pulse-warning 2s ease-in-out infinite;
}

@keyframes pulse-warning {
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
    }
    50% {
        box-shadow: 0 0 0 10px rgba(255, 152, 0, 0);
    }
}

.print-widget .widget-label {
    color: #fff;
}

.print-widget .widget-item {
    color: #fff;
}

.print-widget.printing {
    cursor: not-allowed;
}

.print-widget:not(.printing) .widget-content.clickable:hover {
    transform: translateY(-2px);
}

/* Spinning animation for loading icon */
.widget-icon.spinning {
    animation: spin 1s linear infinite;
}

/* Loading Widget (Grey) */
.loading-widget {
    background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
    border-color: #757575;
    border-style: solid;
    opacity: 0.9;
}

.loading-widget .widget-label {
    color: #424242;
}

.loading-widget .widget-item {
    color: #616161;
}

/* Tracking Widget (Purple) */
.tracking-widget {
    background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
    border-color: #9c27b0;
    animation: pulse-tracking 2s ease-in-out infinite;
}

.tracking-widget .widget-label {
    color: #7b1fa2;
}

.tracking-widget .widget-item {
    color: #4a148c;
}

/* Split Widget (Blue) */
.split-widget {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border-color: #2196f3;
}

.split-widget .widget-label {
    color: #1565c0;
}

.split-widget .widget-item {
    color: #0d47a1;
}

.split-widget:hover {
    background: linear-gradient(135deg, #bbdefb 0%, #90caf9 100%);
}

/* Reset Scan Progress Widget */
.reset-scan-widget {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-color: #ff9800;
}

.reset-scan-widget .widget-label {
    color: #ef6c00;
}

.reset-scan-widget .widget-item {
    color: #e65100;
}

.reset-scan-widget:hover {
    background: linear-gradient(135deg, #ffe0b2 0%, #ffcc02 100%);
    transform: translateY(-1px);
}

/* Multicart Widget (Orange) */
.multicart-widget {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-color: #ff9800;
}

.multicart-widget .widget-label {
    color: #f57c00;
}

.multicart-widget .widget-item {
    color: #e65100;
}

.multicart-widget:hover {
    background: linear-gradient(135deg, #ffe0b2 0%, #ffcc02 100%);
}

/* Package Size Widget */
.package-size-widget {
    display : flex;
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    border-color: #ff9800;
    border-width: 2px;
    min-height: 64px; /* Match other widgets height */
}

.package-size-widget .widget-content {
    align-items: center; /* Vertically center content */
}

.package-size-widget .widget-label {
    color: #f57c00;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0;
}

.package-size-widget .widget-text {
    display: flex;
    flex-direction: row; /* Horizontal layout for label and buttons */
    gap: 12px;
    align-items: center; /* Vertically center */
    flex: 1;
}

.package-size-widget .widget-buttons {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.package-size-widget .size-btn {
    font-size: 11px !important;
    font-weight: 600 !important;
    min-width: 85px !important;
    text-transform: none !important;
    height: 32px !important;
    padding: 0 12px !important;
}

@keyframes pulse-scan {
    0%, 100% {
        box-shadow: 0 2px 8px rgba(255, 152, 0, 0.2);
        border-color: #ff9800;
    }
    50% {
        box-shadow: 0 4px 16px rgba(255, 152, 0, 0.4);
        border-color: #f57c00;
    }
}

@keyframes pulse-pack {
    0%, 100% {
        box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
        border-color: #4caf50;
    }
    50% {
        box-shadow: 0 6px 20px rgba(76, 175, 80, 0.6);
        border-color: #388e3c;
        transform: scale(1.02);
    }
}

@keyframes pulse-print {
    0%, 100% {
        box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
        border-color: #4caf50;
    }
    50% {
        box-shadow: 0 6px 20px rgba(76, 175, 80, 0.6);
        border-color: #388e3c;
        transform: scale(1.02);
    }
}

@keyframes pulse-printing {
    0%, 100% {
        box-shadow: 0 2px 8px rgba(129, 199, 132, 0.4);
        border-color: #66bb6a;
    }
    50% {
        box-shadow: 0 4px 16px rgba(129, 199, 132, 0.7);
        border-color: #4caf50;
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

@keyframes pulse-tracking {
    0%, 100% {
        box-shadow: 0 2px 8px rgba(156, 39, 176, 0.3);
        border-color: #9c27b0;
    }
    50% {
        box-shadow: 0 6px 20px rgba(156, 39, 176, 0.6);
        border-color: #7b1fa2;
        transform: scale(1.02);
    }
}

@keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
        transform: translateY(0);
    }
    40% {
        transform: translateY(-4px);
    }
    60% {
        transform: translateY(-2px);
    }
}

/* Icon animation classes */
.widget-icon.bouncing {
    animation: bounce 2s ease-in-out infinite;
}

/* === RESPONSIVE DESIGN === */

/* Tablet-specific adjustments (768px - 1366px) */
@media (min-width: 768px) and (max-width: 1366px) {
    .packlist-actions-layout,
    .multicart-actions-layout {
        padding: 12px;
        gap: 10px;
    }

    .instruction-widget {
        padding: 10px 14px;
        min-width: 140px;
    }

    .widget-icon {
        font-size: 20px !important;
    }

    .widget-label {
        font-size: 11px;
    }

    .widget-item {
        font-size: 12px;
    }

    .package-size-widget .size-btn {
        min-width: 36px !important;
        height: 36px !important;
        font-size: 12px !important;
        padding: 0 10px !important;
    }

    .right-actions,
    .right-widgets {
        gap: 10px;
    }
}

@media (max-width: 768px) {
    .packlist-actions-layout,
    .multicart-actions-layout {
        flex-direction: column;
        gap: 12px;
        padding: 12px;
    }
    
    .left-controls-with-timer {
        justify-content: center;
    }
    
    .right-actions,
    .right-widgets {
        justify-content: center;
        flex-wrap: wrap;
    }
    
    .instruction-widget {
        min-width: auto;
        padding: 12px;
    }
    
    .widget-icon {
        font-size: 24px !important;
    }
    
    .widget-label {
        font-size: 10px;
    }

    /* Package size widget responsive */
    .package-size-widget .widget-text {
        flex-direction: column;
        gap: 8px;
        align-items: stretch;
    }

    .package-size-widget .widget-buttons {
        width: 100%;
        justify-content: center;
    }

    .package-size-widget .size-btn {
        flex: 1;
        min-width: 80px !important;
    }
    
    .widget-item {
        font-size: 12px;
    }
}

@media (max-width: 480px) {
    .packlist-actions-layout,
    .multicart-actions-layout {
        padding: 8px;
    }
    
    .right-actions,
    .right-widgets {
        gap: 8px;
    }
    
    .instruction-widget {
        flex: 1;
        min-width: auto;
        max-width: 48%;
    }
}
</style>